<!--
 * Path: src/core/components/dialogs/LoginDialog.vue
 * Task: Handles credential-based login with a clean, single-purpose panel.
-->
<template>
  <BaseDialog
    v-model="authStore.isLoginDialogOpen"
    title="Log In"
    max-width="480"
    @close="authStore.isLoginDialogOpen = false"
  >
    <!-- pt-2 added to prevent the outlined floating label from being clipped by v-window overflow -->
    <v-form @submit.prevent="handleLogin" @keydown.enter="handleLogin" class="pt-2">
      <v-text-field
        ref="loginEmailRef"
        v-model="authData.email"
        label="Email Address"
        prepend-inner-icon="mdi-email"
        variant="outlined"
        density="comfortable"
        class="mb-2"
      ></v-text-field>
      <v-text-field
        v-model="authData.password"
        label="Password"
        prepend-inner-icon="mdi-lock"
        type="password"
        variant="outlined"
        density="comfortable"
      ></v-text-field>
    </v-form>

    <div class="text-body-2 text-medium-emphasis mt-2 px-1">
      Don't have an account?
      <a href="#" class="text-primary font-weight-bold text-decoration-none" @click.prevent="switchToRegister">
        Create one here
      </a>
    </div>

    <!-- Footer action controls -->
    <template #actions>
      <v-btn variant="text" @click="authStore.isLoginDialogOpen = false">Cancel</v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        @click="handleLogin"
        :loading="authStore.isLoading"
      >
        Log In
      </v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue';
import { useAuthStore } from '@/store/auth';
import BaseDialog from './BaseDialog.vue';

const authStore = useAuthStore();

const loginEmailRef = ref<any>(null);

const authData = ref({
  email: '',
  password: ''
});

const focusField = (): void => {
  nextTick(() => {
    if (loginEmailRef.value) {
      const el = loginEmailRef.value.$el?.querySelector('input');
      el?.focus();
    }
  });
};

watch(() => authStore.isLoginDialogOpen, (isOpen) => {
  if (isOpen) {
    focusField();
  }
});

const switchToRegister = () => {
  authStore.isLoginDialogOpen = false;
  authStore.isRegisterDialogOpen = true;
};

const handleLogin = async (): Promise<void> => {
  try {
    await authStore.login({
      email: authData.value.email,
      password: authData.value.password
    });
    authData.value.password = '';
    authStore.isLoginDialogOpen = false;
    authStore.showNotification('Successfully logged in.', 'success');
  } catch (error: any) {
    authStore.showNotification(error.message || 'Login failed.', 'error');
  }
};
</script>
